import { useCallback, useState, type MouseEvent, type SyntheticEvent } from 'react'

import { Handle, Position, type Node, type NodeProps } from 'reactflow'
import { config } from '@/config'
import excelIcon from '@/assets/icons/excel.svg'
const restApiIcon = '/assets/img/icons/db/restapi.svg'
const serviceNowIcon = '/assets/img/icons/db/servicenow.svg'
import sftpIcon from '@/assets/icons/sftp.png'

import { MaterialIcon } from '@/ui/material-icon'
import { Button } from '@/ui/button'

import { useChooseDataSource } from '@/features/adp/context/ChooseDataSourceContext'
import { usePipelineFlowState } from '../pipelineFlowStateContext'
import { cardHeaderLabel, duplicateAssetKey } from '@/features/adp/utils/pipelineAssetUtils'
import styles from './flowNodes.module.scss'
import { toast } from 'sonner'

export type AssetNodeData = {
  label: string
  icon?: string
  iconImage?: string
  groupId?: string
  connectorId?: string
  titleLine?: string
  assetKey?: string
  locked?: boolean
}

export type AssetNodeType = Node<AssetNodeData, 'asset'>

const ICON_FILENAME_FALLBACK_MAP: Record<string, string> = {
  'postgresql.svg': 'postgres.svg',
  'mariadb.svg': 'maria-db.svg',
  'mongodb.svg': 'mongo-db.svg',
  'sql-generic.svg': 'mssql.svg',
  postgresql: 'postgres.svg',
  mariadb: 'maria-db.svg',
  mongodb: 'mongo-db.svg',
  sqlserver: 'mssql.svg',
}

const BUNDLED_CONNECTOR_ICON_MAP: Record<string, string> = {
  excel: excelIcon,
  'excel.svg': excelIcon,
  xls: excelIcon,
  xlsx: excelIcon,
  servicenow: serviceNowIcon,
  'servicenow.svg': serviceNowIcon,
  sftp: sftpIcon,
  'sftp.png': sftpIcon,
  realtime: restApiIcon,
  'real-time': restApiIcon,
  realtimeapi: restApiIcon,
  'real time source': restApiIcon,
  'realtime source': restApiIcon,
  realtime_source: restApiIcon,
  'rest api': restApiIcon,
  'restapi-realtime': restApiIcon,
  restapirealtime: restApiIcon,
  restapi_realtime: restApiIcon,
  restapi: restApiIcon,
  rest_api: restApiIcon,
  'api source': restApiIcon,
  api_source: restApiIcon,
  'icon-latest (1).png': restApiIcon,
  api: restApiIcon,
}

const MULTIPLE_DESTINATIONS_NOT_ALLOWED_TOAST = 'Multiple destinations is not allowed'

function headerToneClass(groupId: string | undefined): string {
  switch (groupId) {
    case 'sources':
      return styles.headerSource;

    case 'destinations':
      return styles.headerDestination;

    case 'integrations':
      return styles.headerIntegration;

    case 'transformations':
      return styles.headerTransform;

    default:
      return styles.headerSource;
  }
}

export function AssetNode({ id, data }: NodeProps<AssetNodeData>) {
  const { setNodes, removeNodeAndConnectedEdges } = usePipelineFlowState();
  const chooseDataSource = useChooseDataSource();
  const [expanded, setExpanded] = useState(true);
  const titleLine = data.titleLine ?? data.label;
  const assetKey = data.assetKey ?? '';

  const locked = Boolean(data.locked)
  const normalizedConnector = (data.connectorId ?? '').trim().toLowerCase()
  const normalizedTitle = titleLine.trim().toLowerCase()
  const isRestApiRealtimeNode =
    normalizedTitle === 'restapi-realtime' ||
    normalizedTitle === 'rest api realtime' ||
    normalizedTitle === 'rest api' ||
    normalizedTitle === 'realtime'
  const displayIconImage =
    (isRestApiRealtimeNode
      ? restApiIcon
      : (BUNDLED_CONNECTOR_ICON_MAP[normalizedConnector] ??
        BUNDLED_CONNECTOR_ICON_MAP[normalizedConnector.replace(/[_-]+/g, ' ')] ??
        BUNDLED_CONNECTOR_ICON_MAP[normalizedConnector.replace(/\s+/g, '')])) ?? data.iconImage

  const headerLabel = cardHeaderLabel(data.groupId)
  const handleNodeIconError = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      const target = e.currentTarget
      if (target.dataset.fallback === 'done') return
      const connector = (data.connectorId ?? '').trim().toLowerCase()
      const mapped = ICON_FILENAME_FALLBACK_MAP[connector] ?? `${connector}.svg`
      const apiBase = config.adpApiUrl.replace(/\/api\/v1\/?$/i, '').replace(/\/$/, '')
      const raw = (data.iconImage ?? '').trim()
      const remoteFromRaw =
        raw && !/^https?:\/\//i.test(raw) ? `${apiBase}/${raw.replace(/^\//, '')}` : raw
      const remoteFromConnector = connector ? `${apiBase}/assets/img/icons/db/${mapped}` : ''
      const filename = raw.split('/').pop()?.trim() ?? ''
      const normalizedFilename = ICON_FILENAME_FALLBACK_MAP[filename] ?? filename
      const bundledIcon =
        BUNDLED_CONNECTOR_ICON_MAP[connector] ??
        BUNDLED_CONNECTOR_ICON_MAP[connector.replace(/[_-]+/g, ' ')] ??
        BUNDLED_CONNECTOR_ICON_MAP[connector.replace(/\s+/g, '')] ??
        BUNDLED_CONNECTOR_ICON_MAP[filename.toLowerCase()] ??
        BUNDLED_CONNECTOR_ICON_MAP[normalizedFilename.toLowerCase()]
      const shouldPreferBundled =
        connector === 'excel' ||
        connector === 'xls' ||
        connector === 'xlsx' ||
        filename.toLowerCase() === 'excel.svg'
      const localSvg = normalizedFilename ? `/assets/img/icons/db/${normalizedFilename}` : ''
      const localPng = normalizedFilename
        ? `/assets/img/icons/db/${normalizedFilename.replace('.svg', '.png')}`
        : ''

      if (target.dataset.fallback !== 'raw' && remoteFromRaw && remoteFromRaw !== target.src) {
        target.dataset.fallback = 'raw'
        target.src = remoteFromRaw
        return
      }

      if (
        shouldPreferBundled &&
        target.dataset.fallback !== 'bundled' &&
        bundledIcon &&
        bundledIcon !== target.src
      ) {
        target.dataset.fallback = 'bundled'
        target.src = bundledIcon
        return
      }

      if (
        target.dataset.fallback !== 'connector' &&
        remoteFromConnector &&
        remoteFromConnector !== target.src
      ) {
        target.dataset.fallback = 'connector'
        target.src = remoteFromConnector
        return
      }

      if (target.dataset.fallback !== 'local-svg' && localSvg && localSvg !== target.src) {
        target.dataset.fallback = 'local-svg'
        target.src = localSvg
        return
      }

      if (target.dataset.fallback !== 'local-png' && localPng && localPng !== target.src) {
        target.dataset.fallback = 'local-png'
        target.src = localPng
        return
      }

      if (target.dataset.fallback !== 'bundled' && bundledIcon && bundledIcon !== target.src) {
        target.dataset.fallback = 'bundled'
        target.src = bundledIcon
        return
      }

      target.dataset.fallback = 'done'
    },
    [data.connectorId, data.iconImage]
  )

  const handleSettings = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()

      if (data.groupId === 'sources' || data.groupId === 'destinations') {
        chooseDataSource?.openChooseDataSource(id, data.connectorId, data.groupId)
        return;
      }

      if (data.groupId === 'integrations') {
        chooseDataSource?.openIntegrationSettings(id, data.connectorId)
        return;
      }

      if (data.groupId === 'transformations') {
        chooseDataSource?.openTransformationSettings(id, data.connectorId);
      }
    },

    [chooseDataSource, id, data.groupId, data.connectorId]
  )

  const handleToggleExpand = useCallback((e: MouseEvent) => {
    e.stopPropagation()

    setExpanded(v => !v)
  }, [])

  const handleLock = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()

      setNodes(nds =>
        nds.map(n => {
          if (n.id !== id) return n

          const nextLocked = !(n.data as AssetNodeData).locked
          return {
            ...n,
            draggable: !nextLocked,
            data: { ...n.data, locked: nextLocked },
          }
        })
      )
    },

    [id, setNodes]
  )

  const handleDuplicate = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()

      setNodes(nds => {
        const self = nds.find(n => n.id === id);
        if (!self) return nds;

        const d = self.data as AssetNodeData;
        if (d.groupId === 'destinations') {
          toast.error(MULTIPLE_DESTINATIONS_NOT_ALLOWED_TOAST)
          return nds
        }
        const newKey = duplicateAssetKey(d.titleLine ?? d.label, d.groupId, d.assetKey);

        const clone: Node = {
          id: `dropped-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          type: self.type,
          position: { x: self.position.x + 36, y: self.position.y + 36 },
          sourcePosition: self.sourcePosition,
          targetPosition: self.targetPosition,
          className: self.className,
          style: self.style,
          hidden: self.hidden,
          selectable: self.selectable,
          connectable: self.connectable,
          draggable: true,
          data: { ...d, assetKey: newKey, locked: false },
        }

        return nds.concat(clone)
      })
    },

    [id, setNodes]
  )

  const handleDelete = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()

      removeNodeAndConnectedEdges(id)
    },

    [removeNodeAndConnectedEdges, id]
  )

  return (
    <div className={styles.assetCard}>
      {data.groupId !== 'sources' && (
        <Handle id="target" type="target" position={Position.Left} className={styles.handle} />
      )}
      <div className={styles.assetCardInner}>
        {expanded ? (
          <>
            {/* ── EXPANDED: full card with header / body / footer ── */}
            <div className={`${styles.header} ${headerToneClass(data.groupId)}`}>
              <div className={styles.headerLeft}>
                <span className={styles.headerTitle}>{headerLabel}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${styles.headerIconBtn} nodrag`}
                  aria-label="Settings"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={handleSettings}
                >
                  <MaterialIcon name="settings" size={16} />
                </Button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.headerIconBtn} nodrag`}
                aria-expanded={expanded}
                aria-label="Collapse"
                onPointerDown={e => e.stopPropagation()}
                onClick={handleToggleExpand}
              >
                <MaterialIcon name="unfold_more" size={18} />
              </Button>
            </div>

            <div className={styles.body}>
              <div className={styles.logoWell}>
                {displayIconImage && (
                  <img
                    src={displayIconImage}
                    alt=""
                    className={styles.logoImg}
                    onError={handleNodeIconError}
                  />
                )}
                {!displayIconImage && data.icon && (
                  <MaterialIcon name={data.icon} size={20} className={styles.logoGlyph} />
                )}
              </div>
              <div className={styles.bodyText}>
                <div className={styles.bodyTitle}>{titleLine}</div>
                <div className={styles.bodySubtitle}>{assetKey}</div>
              </div>
            </div>

            <div className={styles.footer}>
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.footerBtn} nodrag`}
                aria-label={locked ? 'Unlock node' : 'Lock node'}
                aria-pressed={locked}
                onPointerDown={e => e.stopPropagation()}
                onClick={handleLock}
              >
                <MaterialIcon name={locked ? 'lock' : 'lock_open'} size={18} />
              </Button>
              <div className={styles.footerRight}>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${styles.footerBtn} nodrag`}
                  aria-label="Duplicate"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={handleDuplicate}
                >
                  <MaterialIcon name="content_copy" size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${styles.footerBtn} ${styles.footerDelete} nodrag`}
                  aria-label="Delete"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={handleDelete}
                >
                  <MaterialIcon name="delete" size={18} />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* ── COLLAPSED: header strip + body with icon/title ── */}
            <div className={`${styles.header} ${headerToneClass(data.groupId)}`}>
              <div className={styles.headerLeft}>
                <span className={styles.headerTitle}>{headerLabel}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.headerIconBtn} nodrag`}
                aria-expanded={false}
                aria-label="Expand"
                onPointerDown={e => e.stopPropagation()}
                onClick={handleToggleExpand}
              >
                <MaterialIcon name="unfold_more" size={18} />
              </Button>
            </div>

            <div className={styles.body}>
              <div className={styles.logoWell}>
                {data.iconImage && (
                  <img
                    src={data.iconImage}
                    alt=""
                    className={styles.logoImg}
                    onError={handleNodeIconError}
                  />
                )}
                {!data.iconImage && data.icon && (
                  <MaterialIcon name={data.icon} size={20} className={styles.logoGlyph} />
                )}
              </div>
              <div className={styles.bodyText}>
                <div className={styles.bodyTitle}>{titleLine}</div>
                <div className={styles.bodySubtitle}>{assetKey}</div>
              </div>
            </div>
          </>
        )}
      </div>
      {data.groupId !== 'destinations' && (
        <Handle id="source" type="source" position={Position.Right} className={styles.handle} />
      )}
    </div>
  )
}
